import { createSlice } from '@reduxjs/toolkit'
import {
  fetchProperties,
  createProperty,
  updateProperty,
  deleteProperty,
  fetchProperty,
  fetchPropertiesbyAgent,
  savedProperties,
} from '../actions/propertyActions.js'

const initialState = {
  properties: [],
  currentProperty: null,
  saved: [],
  agentProperties: [],
  loading: false,
  error: null,
}

const propertySlice = createSlice({
  name: 'property',
  initialState,
  reducers: {
    resetPropertyState: state => {
      state.properties = []
      state.loading = false
      state.error = null
    },
    setStatusMessage: (state, action) => {
      state.statusMessage = action.payload
    },
    getPropertyRequest: state => {
      state.loading = true
      state.error = null
    },
    getPropertySuccess: (state, action) => {
      state.loading = false
      state.currentProperty = action.payload
    },
    getPropertyFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    updatePropertyStatusRequest: state => {
      state.loading = true
      state.error = null
    },
    updatePropertyStatusSuccess: (state, action) => {
      state.loading = false
      state.currentProperty = action.payload
    },
    updatePropertyStatusFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
    patchPropertyRequest: state => {
      state.loading = true
      state.error = null
    },
    patchPropertySuccess: (state, action) => {
      state.loading = false
      state.currentProperty = action.payload
    },
    patchPropertyFail: (state, action) => {
      state.loading = false
      state.error = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProperties.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProperties.fulfilled, (state, action) => {
        state.properties = action.payload
        state.loading = false
      })
      .addCase(fetchProperties.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(fetchProperty.pending, state => {
        state.loading = true
        state.currentProperty = null
        state.error = null
      })
      .addCase(fetchProperty.fulfilled, (state, action) => {
        state.currentProperty = action.payload // Store single property separately
        state.loading = false
      })
      .addCase(fetchProperty.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(savedProperties.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(savedProperties.fulfilled, (state, action) => {
        state.loading = false
        state.saved = action.payload
      })
      .addCase(savedProperties.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(fetchPropertiesbyAgent.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchPropertiesbyAgent.fulfilled, (state, action) => {
        state.agentProperties = action.payload
        state.loading = false
      })
      .addCase(fetchPropertiesbyAgent.rejected, (state, action) => {
        state.error = action.payload
        state.loading = false
      })
      .addCase(createProperty.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(createProperty.fulfilled, (state, action) => {
        state.properties.push(action.payload)
        state.loading = false
      })
      .addCase(createProperty.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })
      .addCase(updateProperty.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProperty.fulfilled, (state, action) => {
        const updatedProperty = action.payload
        const propertiesIndex = state.properties.findIndex(property => property._id === updatedProperty._id)
        if (propertiesIndex !== -1) {
          // Merge changes instead of full replace
          state.properties[propertiesIndex] = {
            ...state.properties[propertiesIndex],
            ...updatedProperty,
          }
        }

        // Update currentProperty if it's the active one
        if (state.currentProperty?._id === updatedProperty._id) {
          state.currentProperty = {
            ...state.currentProperty,
            ...updatedProperty,
          }
        }

        state.loading = false
      })
      .addCase(updateProperty.rejected, (state, action) => {
        state.error = action.error.message
        state.loading = false
      })
      .addCase(deleteProperty.fulfilled, state => {
        state.currentProperty = null
        state.loading = false
      })
  },
})

export const {
  resetPropertyState,
  setStatusMessage,
  getPropertyRequest,
  getPropertySuccess,
  getPropertyFail,
  getPropertybyAgentRequest,
  getPropertybyAgentSuccess,
  getPropertybyAgentFail,
  updatePropertyStatusRequest,
  updatePropertyStatusSuccess,
  updatePropertyStatusFail,
  patchPropertyRequest,
  patchPropertySuccess,
  patchPropertyFail,
} = propertySlice.actions

export default propertySlice.reducer
